/**
 * Line-item status markers (HS2-K9TACV). A line item can carry several status
 * markers at once — the AI-robot is just one of them. Kept as a pure leaf module
 * (no DOM, no icons) so the marker logic is unit-testable; the view maps each
 * marker `kind` to its icon and colour.
 */
import { isAiDetected, type Provenance } from './provenance.js';

export type LineStatusKind = 'ai-detected' | 'needs-clarification';

export interface LineStatusMarker {
  kind: LineStatusKind;
  /** Accessible label / tooltip text for the icon. */
  label: string;
}

/** The status the marker logic reads from a line item. */
export interface LineStatusInput {
  provenance: Provenance | null | undefined;
  needsClarification?: boolean;
  description?: string | null;
  quantity?: string | null;
  unit?: string | null;
}

/** The longest question excerpt a clarification reason quotes. */
export const CLARIFICATION_QUESTION_MAX = 120;

/** A note as the line's notes list serves it: who wrote it, when, and its Markdown body. */
export interface ClarificationNote {
  lineItemId: string | null;
  authorMemberId: string | null;
  body: string;
  createdAt: string;
}

/**
 * The reviewer's unanswered question on a line (HS2-4YCV66): the newest staff note on it, when the
 * requester has not written on the line since. Staff are everyone but the demand's requester, as in
 * the server's resubmit rule (`clearAnsweredClarifications`, HS2-R5H22P); a demand without a
 * requester counts every note as staff. Returns undefined when nobody is waiting on an answer.
 */
export function pendingClarificationQuestion(
  lineId: string,
  notes: readonly ClarificationNote[],
  requesterMemberId: string | null | undefined,
): ClarificationNote | undefined {
  const requester = requesterMemberId ?? null;
  let lastStaff: ClarificationNote | undefined;
  let lastRequester: ClarificationNote | undefined;
  for (const note of notes) {
    if (note.lineItemId !== lineId) continue;
    const fromRequester = requester !== null && note.authorMemberId === requester;
    const latest = fromRequester ? lastRequester : lastStaff;
    if (latest !== undefined && note.createdAt.localeCompare(latest.createdAt) < 0) continue;
    if (fromRequester) lastRequester = note;
    else lastStaff = note;
  }
  if (lastStaff === undefined) return undefined;
  if (lastRequester !== undefined && lastRequester.createdAt.localeCompare(lastStaff.createdAt) > 0) return undefined;
  return lastStaff;
}

/** A one-line plain-text excerpt of a Markdown note body, cut at a word near `max` characters. */
export function noteExcerpt(body: string, max = CLARIFICATION_QUESTION_MAX): string {
  const text = body
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1') // links and images keep their text
    .replace(/[*_`~#>]+/g, '') // emphasis, code, headings, quotes
    .replace(/\s+/g, ' ')
    .trim();
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const space = cut.lastIndexOf(' ');
  return `${(space > max / 2 ? cut.slice(0, space) : cut).trimEnd()}…`;
}

/** Human-readable reasons behind the persisted clarification flag. A reviewer's open question
 * leads (HS2-4YCV66), then vendors that disagree on the unit, then concrete missing or
 * low-confidence facts; the generic fallback appears only when there is none of these.
 *
 * A missing unit or quantity alone is not a reason (HS2-T1N9FJ): it shows as a quiet
 * {@link lineDetailHints} hint instead. A flag set only for a missing unit or quantity
 * (as older extraction runs did) therefore yields no reasons, no marker and no alert. */
export function lineClarificationReasons(line: LineStatusInput, question?: string): string[] {
  if (line.needsClarification !== true) return [];
  const reasons: string[] = [];
  const asked = question === undefined ? '' : noteExcerpt(question);
  if (asked !== '') reasons.push(`Clarification requested: “${asked}”`);
  if (line.provenance?.retainedAfterReextract === true)
    reasons.push(
      'This line was kept after document re-extraction because a confirmed quote refers to it. Review the line and quote mapping.',
    );
  if (line.provenance?.retainedAfterDocumentMove === true)
    reasons.push(
      'This line was kept after its source document changed type because a confirmed quote refers to it. Review the line and quote mapping.',
    );
  const conflict = line.provenance?.unitConflict ?? [];
  if (conflict.length > 1)
    reasons.push(`Vendors quote different units (${conflict.join(', ')}). Set the unit to compare.`);
  if ((line.description ?? '').trim() === '') reasons.push('Description is missing.');
  const confidence = line.provenance?.confidence;
  const missingDetailOnly = (line.quantity ?? '').trim() === '' || (line.unit ?? '').trim() === '';
  if (reasons.length === 0 && missingDetailOnly) return [];
  const inferred = reasons.length - (asked === '' ? 0 : 1);
  if (inferred === 0 && confidence !== undefined && confidence < 0.5) {
    const formattedConfidence = new Intl.NumberFormat(undefined, {
      style: 'percent',
      maximumFractionDigits: 0,
    }).format(confidence);
    reasons.push(`AI extraction confidence is low (${formattedConfidence}). Confirm the line details.`);
  }
  if (reasons.length === 0) reasons.push('Description or specification needs confirmation.');
  return reasons;
}

/**
 * Quiet, non-alerting notes about a line's unit and quantity (HS2-T1N9FJ): where a
 * filled-in value came from, or that one is still not stated. Never shown for a
 * vendor unit conflict, which is a clarification reason instead.
 */
export function lineDetailHints(line: LineStatusInput): string[] {
  const p = line.provenance;
  const hints: string[] = [];
  if (p?.sourceDocumentDeleted === true)
    hints.push(
      p.sourceDocumentName === undefined
        ? 'The source document for this extracted line was deleted.'
        : `Source document ${p.sourceDocumentName} was deleted.`,
    );
  const hasUnit = (line.unit ?? '').trim() !== '';
  const hasQuantity = (line.quantity ?? '').trim() !== '';
  if (hasUnit && p?.unitSource === 'quote') hints.push(`Unit from ${p.unitVendor ?? 'a vendor'}'s quote.`);
  else if (hasUnit && p?.unitSource === 'inferred') hints.push(`Unit assumed (${line.unit ?? ''}).`);
  if (hasQuantity && p?.quantitySource === 'quote')
    hints.push(`Quantity from ${p.quantityVendor ?? 'a vendor'}'s quote.`);
  const conflict = (p?.unitConflict ?? []).length > 1;
  if (!hasUnit && !conflict) hints.push('Unit not stated.');
  if (!hasQuantity) hints.push('Quantity not stated.');
  return hints;
}

/** The status markers a line item currently warrants, in a stable display order.
 *  Empty when the line has no notable status. */
export function lineStatusMarkers(l: LineStatusInput, question?: string): LineStatusMarker[] {
  const markers: LineStatusMarker[] = [];
  if (isAiDetected(l.provenance))
    markers.push({
      kind: 'ai-detected',
      label:
        l.provenance?.sourceDocumentDeleted === true
          ? 'Detected by the assistant; source document deleted'
          : 'Detected by the assistant',
    });
  const reasons = lineClarificationReasons(l, question);
  if (reasons.length > 0) markers.push({ kind: 'needs-clarification', label: reasons.join(' ') });
  return markers;
}
